/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import {Rule} from "../../models/rule";
import { EmGroup } from '../../models/em-group';
import { GroupType } from '../../models/group-type';

import { RulesService } from "../../services/rules.service";
import { GroupService } from '../../services/group.service';

import {OnInit, Component, ViewChild} from "@angular/core";
import {ActivatedRoute, Router} from "@angular/router";

import { AddToGroupDialogComponent } from "../add-to-group-dialog.component";
import { ManageGroupsDialogComponent } from "../manage-groups-dialog.component";
import { RemoveFromGroupDialogComponent } from "../remove-from-group-dialog.component";
import { PagedResults } from "app/models/paged-results";
import { RESULTS_PER_PAGE } from "app/app-constants";
import { ERROR_DIALOG_TITLE, SimpleSuccessErrorDialogComponent } from "../simple-success-error-dialog-box/simple-success-error-dialog-box.component";

@Component({
  templateUrl: './rules.component.html',
  styleUrls: ['../../../assets/css/infrastructure.css']
})
export class RulesComponent implements OnInit {

  @ViewChild('addToGroupDialog')
  addToGroupDialogComponent: AddToGroupDialogComponent;

  @ViewChild('removeFromGroupDialog')
  removeFromGroupDialogComponent: RemoveFromGroupDialogComponent;

  @ViewChild('manageGroupsDialog')
  manageGroupsDialog: ManageGroupsDialogComponent;

  /** Reference to the SimpleSuccessErrorDialog used to display error */
  @ViewChild('successErrorDialog') 
  simpleSuccessErrorDialogComponent: SimpleSuccessErrorDialogComponent;

  //Rules shown in the UI
  mRules: Rule[] = [];
  selectedRules: RuleUI[] = [];

  filterColumnRule = null;
  filterColumnNameRule = "";
  filterStringRule = "";

  // paginator variables
  //pageSize = 2;
  numberOfRules = 0;
  totalNumberOfRules = 0;
  currentPageForRules = 1;

  // List for Dropdown of Results Per Page value
  resultsPerPageValues = RESULTS_PER_PAGE;
  selectedPageSizeValueForRules: number = this.resultsPerPageValues[0];


  constructor(
    private mRoute: ActivatedRoute,
    private groupService: GroupService,
    private mRouter: Router,
    private mRulesService: RulesService
  ) {}

  ngOnInit(): void {
    //Subscribe to the Route to get the Rules that get loaded as it is resolved
    this.mRoute.data.subscribe((data: {rules: PagedResults<Rule>}) => {
      this.selectedRules = [];

      //Set the initial values for Pagination
      this.selectedPageSizeValueForRules = this.resultsPerPageValues[0];

      for (const rule of data.rules.data) {
        const ruleUI = new RuleUI();
        ruleUI.rule = rule;
        this.selectedRules.push(ruleUI);
      }
      //Set the total Number of Rules. Earlier the size was set based only to the count of rules for one page.
      this.totalNumberOfRules = data.rules.size;
    });
  }

  manageResultPerPageClickedForRules(value: number): void {
    this.selectedPageSizeValueForRules = value;
    // selecting any value will bring you back to first page
    this.currentPageForRules = 1;
    this.refreshRules();
  }

  addClicked(): void {
    console.log("addClicked()");
    this.mRouter.navigateByUrl(`/rules/editor/`);
  }

  editRule(rule: Rule): void {
    console.log("editRule() - Rule: " + (rule == null ? "*NULL*" : (rule.id + "/" + rule.name)));
    if (rule != null) {
      this.mRouter.navigateByUrl(`/rules/editor/${rule.id}`);
    }
  }

  deleteRule(rule: Rule): void {
    console.log("deleteRule() - Rule: " + (rule == null ? "*NULL*" : (rule.id + "/" + rule.name)));
    this._deleteRule(rule);
  }

  private async refreshRules(): Promise<void> {
    await this.mRulesService.getAllRulesForPaging((this.currentPageForRules - 1) * this.selectedPageSizeValueForRules, this.selectedPageSizeValueForRules, 'ASC', this.filterStringRule).then(
      responses => {
        this.selectedRules = [];
        for(const response of responses.data) {
          const ruleUI = new RuleUI();
          ruleUI.rule = response;
          this.selectedRules.push(ruleUI);
        }
        this.totalNumberOfRules = responses.size;
      },
      error => {
        console.log("Error calling getAllRulesForPaging");
      }
    );
  }

  private async _deleteRule(rule: Rule): Promise<void> {
    try {
      await this.mRulesService.deleteRuleById(rule.id);
      const index = this.mRules.indexOf(rule);
      if (index > -1) {
        this.mRules.splice(index, 1);
      }
    } catch (error) {
      this.handleError(error);
    }

    this.refreshRules();
  }

  deactivateRule(rule: Rule): void {
    console.log("deactivateRule() - Rule: " + (rule == null ? "*NULL*" : (rule.id + "/" + rule.name)));
    this._deactivateRule(rule);
  }

  private async _deactivateRule(rule: Rule): Promise<void> {
    try {
      await this.mRulesService.stopRule(rule.id);
      rule.isActive = false;
    } catch (error) {
      this.handleError(error);
    }
  }

  activateRule(rule: Rule): void {
    console.log("activateRule() - Rule: " + (rule == null ? "*NULL*" : (rule.id + "/" + rule.name)));
    this._activateRule(rule);
  }

  private async _activateRule(rule: Rule): Promise<void> {
    try {
      await this.mRulesService.startRule(rule.id);
      rule.isActive = true;
    } catch (error) {
      this.handleError(error);
    }
  }

  handleError(error: any): void {
    console.error("handleError() - Error: " + error);
    if (error != null) {
      const response: Response = error;
      this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE, response.text().toString());
    }
  }

  isEmptySelection(): boolean {
    return this.selectedRules.filter(rule => rule.selected).length == 0;
  }

  isAllSelected(): boolean {
    return this.selectedRules.every(rule => rule.selected);
  }

  selectAll(event: any): void {
    this.selectedRules.forEach(rule => rule.selected = event.target.checked);
  }

  onAddGroups(): void {
    if (!this.isEmptySelection()) {
      this.addToGroupDialogComponent.show(GroupType.RULE, this.getSelectedNames());
     }
  }

  addToGroup(group: EmGroup): void {
    this.groupService.addItemsToGroup(group.id, this.getSelectedIds()).then(
     success => {
        this.refreshRules();
      },
      error => {
        this.handleError(error);
      }
    );
  }

  onRemoveGroup(): void {
    if (!this.isEmptySelection()) {
        const ruleNames: string[] = [];
        const groupNames: string[] = [];
        const groups: EmGroup[] = [];

        this.getSelectedRules().forEach(
            rule => {
                ruleNames.push(rule.name);

                if (rule.groups != null) {
                    rule.groups.forEach(
                        group => {
                            if (!groupNames.includes(group.name)) {
                                groupNames.push(group.name);
                                groups.push(group);
                            }
                        }
                    );
                }
            }
        );

        this.removeFromGroupDialogComponent.show(groups, ruleNames);
    }
  }

  removeFromGroup(group: EmGroup): void {
    this.groupService.removeItemsFromGroup(group.id, this.getSelectedIds()).then(
        success => {
            this.refreshRules();
        },
        error => {
            this.handleError(error);
        }
    );
  }

  onManageGroups(): void {
    this.manageGroupsDialog.show(GroupType.RULE);
  }

  getSelectedRules(): Rule[] {
    const rules: Rule[] = [];
    this.selectedRules.filter(ui => ui.selected).forEach(ui => rules.push(ui.rule));
    return rules;
  }

  getSelectedIds(): number[] {
    const ids: number[] = [];
    this.selectedRules.filter(ui => ui.selected).forEach(ui => ids.push(ui.rule.id));
    return ids;
  }

  getSelectedNames(): string[] {
    const names: string[] = [];
    this.selectedRules.filter(ui => ui.selected).forEach(ui => names.push(ui.rule.name));

    return names;
  }

  pageChanged(pageNumber: number): void {
    if(pageNumber == null || pageNumber == undefined || pageNumber < 1) {
      return;
    }
    if (this.currentPageForRules == pageNumber) {
      return;
    }
    this.currentPageForRules = pageNumber;
    this.refreshRules();
  }

  setFilterRule(filterColumnName: string, filterColumn: string): void {
    this.filterColumnRule = filterColumn;
    this.filterColumnNameRule = filterColumnName;
    if (!this.filterColumnRule) {
      this.filterStringRule = "";
    }
    this.filterStringChangedRule(1);
  }


  filterStringChangedRule(pageNumber?: number): void {
    if (!pageNumber) { pageNumber = 1; }
    // Show only size on this new page
    console.log("page Changed to " + pageNumber);
    this.currentPageForRules = pageNumber;

    this.refreshRules();

  }


}

class RuleUI {
    selected = false;
    rule: Rule;
}
